import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const CommercialPage = () => {
  const benefits = [
    {
      icon: 'ri-line-chart-line',
      title: 'Maximize ROI',
      description: 'Commercial solar systems typically pay for themselves in 5-7 years with ongoing savings for decades.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Tax Advantages',
      description: 'Take advantage of federal ITC, accelerated depreciation, and state incentives to reduce costs.',
    },
    {
      icon: 'ri-funds-line',
      title: 'Reduce Operating Costs',
      description: 'Lower your overhead with predictable energy costs and protection from utility rate increases.',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Corporate Sustainability',
      description: 'Demonstrate environmental leadership and attract eco-conscious customers and employees.',
    },
  ];

  const industries = [
    {
      icon: 'ri-store-2-line',
      title: 'Retail & Shopping Centers',
      description: 'Large roof spaces perfect for solar arrays that reduce operational costs.',
    },
    {
      icon: 'ri-building-line',
      title: 'Office Buildings',
      description: 'Lower energy bills while showcasing your commitment to sustainability.',
    },
    {
      icon: 'ri-hotel-line',
      title: 'Hospitality & Hotels',
      description: 'Reduce overhead costs and appeal to environmentally conscious guests.',
    },
    {
      icon: 'ri-hospital-line',
      title: 'Healthcare Facilities',
      description: 'Reliable backup power and significant energy cost savings for medical facilities.',
    },
    {
      icon: 'ri-truck-line',
      title: 'Warehouses & Distribution',
      description: 'Massive roof areas ideal for large-scale solar installations.',
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Restaurants & Food Service',
      description: 'Offset high energy consumption from refrigeration and cooking equipment.',
    },
  ];

  return (
    <div className="font-poppins">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/67da71b94779fb9943ef0d2b6c7eed00.jpg"
            alt="Commercial building with solar array"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-solar-blue/75 via-solar-blue/55 to-solar-blue/75"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Smart Solar Solutions<br />
            <span className="text-energy-yellow">for Businesses</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto"
          >
            Reduce operating costs, maximize tax benefits, and demonstrate environmental leadership with commercial solar energy.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/contact"
              className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Request Commercial Assessment
            </Link>
            <a
              href="tel:352-509-6933"
              className="bg-white text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              352-509-6933
            </a>
          </motion.div>
        </div>
      </section>

      {/* ROI Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Why <strong>Commercial Solar</strong> Makes Business Sense
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Solar energy is one of the smartest investments your business can make.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                  <i className={`${benefit.icon} text-3xl text-solar-blue`}></i>
                </div>
                <h3 className="text-xl font-bold text-solar-blue mb-3">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Benefits */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
                Powerful <strong>Financial Incentives</strong>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Commercial solar installations qualify for substantial tax benefits and incentives that dramatically reduce your upfront investment.
              </p>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-percent-line text-2xl text-eco-green"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-solar-blue mb-2">30% Federal Tax Credit</h4>
                      <p className="text-sm text-gray-600">The Investment Tax Credit (ITC) allows you to deduct 30% of your solar installation costs from federal taxes.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-calendar-line text-2xl text-eco-green"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-solar-blue mb-2">Accelerated Depreciation</h4>
                      <p className="text-sm text-gray-600">MACRS allows you to depreciate 85% of your system cost over 5 years, providing significant tax savings.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-government-line text-2xl text-eco-green"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-solar-blue mb-2">State & Local Incentives</h4>
                      <p className="text-sm text-gray-600">Florida offers additional rebates and incentives for commercial solar installations.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-2xl"
            >
              <h3 className="text-3xl font-bold text-solar-blue mb-8 text-center">Average Business Savings</h3>
              <div className="space-y-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-energy-yellow mb-2">$2,500+</div>
                  <div className="text-sm text-gray-600">Monthly Energy Savings</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-energy-yellow mb-2">5-7 Years</div>
                  <div className="text-sm text-gray-600">Typical Payback Period</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-energy-yellow mb-2">$750K+</div>
                  <div className="text-sm text-gray-600">25-Year Savings</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Industries We <strong>Serve</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide custom solar solutions for businesses across all sectors.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-eco-green/10 rounded-lg mb-6">
                  <i className={`${industry.icon} text-3xl text-eco-green`}></i>
                </div>
                <h3 className="text-xl font-bold text-solar-blue mb-3">{industry.title}</h3>
                <p className="text-sm text-gray-600">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom System Design */}
      <section className="py-20 bg-solar-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Solar%20engineers%20reviewing%20commercial%20building%20blueprints%20and%20solar%20panel%20layout%20plans%2C%20professional%20team%20meeting%2C%20technical%20drawings%2C%20system%20design%20consultation%2C%20business%20planning%2C%20modern%20office%20setting%2C%20collaborative%20work%20environment%2C%20expertise%20and%20professionalism&width=800&height=1000&seq=commercial-design&orientation=portrait"
                  alt="Custom system design"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Custom <strong>System Design</strong>
              </h2>
              <p className="text-lg text-white/90 mb-8">
                Every business is unique, and so are our solar solutions. We design custom systems optimized for your specific energy needs, building characteristics, and financial goals.
              </p>

              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Comprehensive Energy Analysis</h4>
                    <p className="text-sm text-white/80">We analyze your current energy usage patterns to design the optimal system size.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Structural Assessment</h4>
                    <p className="text-sm text-white/80">Our engineers evaluate your roof or property to ensure safe, efficient installation.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Financial Modeling</h4>
                    <p className="text-sm text-white/80">Detailed ROI projections including all incentives, savings, and payback timelines.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Scalable Solutions</h4>
                    <p className="text-sm text-white/80">Systems designed to grow with your business needs and future expansion plans.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Energy Cost Reduction */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Dramatic <strong>Energy Cost Reduction</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Commercial solar installations typically reduce energy costs by 50-75%, with savings starting immediately.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-10 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-solar-blue mb-8 text-center">Sample 50,000 sq ft Building</h3>
              
              <div className="space-y-6 mb-8">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">Current Annual Energy Cost</span>
                    <span className="text-xl font-bold text-red-500">$36,000</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className="bg-red-500 h-4 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">With Solar Energy Cost</span>
                    <span className="text-xl font-bold text-eco-green">$9,000</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className="bg-eco-green h-4 rounded-full" style={{ width: '25%' }}></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-soft-gray p-6 rounded-xl text-center">
                  <div className="text-3xl font-bold text-solar-blue mb-2">$27,000</div>
                  <div className="text-sm text-gray-600">Annual Savings</div>
                </div>
                <div className="bg-soft-gray p-6 rounded-xl text-center">
                  <div className="text-3xl font-bold text-solar-blue mb-2">75%</div>
                  <div className="text-sm text-gray-600">Cost Reduction</div>
                </div>
                <div className="bg-soft-gray p-6 rounded-xl text-center">
                  <div className="text-3xl font-bold text-solar-blue mb-2">6.2 Years</div>
                  <div className="text-sm text-gray-600">Payback Period</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
              Ready to Reduce Your Business Energy Costs?
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Get a comprehensive commercial solar assessment and discover your potential savings and ROI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                Request Commercial Assessment
              </Link>
              <a
                href="tel:352-509-6933"
                className="bg-solar-blue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-solar-blue/90 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call 352-509-6933
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommercialPage;